import { useState, useEffect } from 'react';

import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { useTimeZone } from '@entities/datetime';
import { useBulkResendCallbackOrderMutation } from '@entities/orders';
import { selectDownloading } from '@features/loading';
import { transformCurrentFilter } from '@features/table-filter';
import { useTableContext } from '@shared/components/Table';
import { isApiError } from '@shared/types/fetch-data';

import { useWebSocketDownload } from './useWebsocketDownload';

export const useBulkActions = <T>(currentFilter: T) => {
  const timezone = useTimeZone();
  const [isError, setIsError] = useState(false);
  const [error, setError] = useState(null);
  const [isSuccess, setIsSuccess] = useState(false);

  const { selectedIds, setSelectedIds } = useTableContext();

  const isDownloadLoading = useSelector(selectDownloading);

  const {
    initiateOrdersExportDownload,
    isError: isWebSocketDownloadError,
    error: webSocketDownloadError,
  } = useWebSocketDownload();

  const [
    bulkResendCallback,
    {
      isLoading: isResendCallbackLoading,
      isError: isResendCallbackError,
      error: resendCallbackError,
      isSuccess: isResendCallbackSuccess,
    },
  ] = useBulkResendCallbackOrderMutation();

  useEffect(() => {
    if (isResendCallbackLoading || isDownloadLoading) {
      setIsError(false);
      setError(null);
      setIsSuccess(false);
    }
  }, [isDownloadLoading, isResendCallbackLoading]);

  useEffect(() => {
    if (isResendCallbackError) {
      setIsError(true);
      setError(resendCallbackError);
    }
    if (isWebSocketDownloadError) {
      setIsError(true);
      setError(webSocketDownloadError);
    }
  }, [
    isResendCallbackError,
    resendCallbackError,
    isWebSocketDownloadError,
    webSocketDownloadError,
  ]);

  useEffect(() => {
    if (isResendCallbackSuccess) {
      setIsSuccess(true);
    }
  }, [isResendCallbackSuccess]);

  useEffect(() => {
    if (isSuccess) {
      setSelectedIds([]);

      toast.success('Resend callbacks order successfully', {
        onOpen: () => {
          setIsSuccess(false);
        },
      });
    }

    if (isError && error && isApiError(error)) {
      toast.error(error.data.message, {
        onOpen: () => {
          setIsError(false);
        },
      });
    }
  }, [isSuccess, isError, error, setIsError, setIsSuccess, setSelectedIds]);

  const handleAction = async (action: string) => {
    toast.dismiss();

    const mappedFilter = transformCurrentFilter<T>(currentFilter);
    const hasSelectedIds = selectedIds.length > 0;
    const hasFilter = Object.keys(mappedFilter).length > 0;

    const uploadFilter = hasSelectedIds
      ? { filter: { ids: selectedIds } }
      : hasFilter
        ? { filter: mappedFilter }
        : {};

    switch (action) {
      case 'resend-callback':
        bulkResendCallback({ ids: selectedIds });
        break;

      case 'download-orders-deposit':
        await initiateOrdersExportDownload(
          {
            ...uploadFilter,
            timezone,
          },
          'deposits',
        );
        break;

      case 'download-orders-withdrawal':
        await initiateOrdersExportDownload(
          {
            ...uploadFilter,
            timezone,
          },
          'withdrawals',
        );
        break;

      default:
        break;
    }
  };

  return {
    handleAction,
    isResendCallbackLoading,
    isDownloadLoading,
  };
};
